import {
    Boxes,
    ClipboardCheck,
    FileCheck2,
    FileInput,
    FileOutput,
    FileText,
    PackageSearch,
    Pill,
    Stethoscope,
    UserRound,
} from 'lucide-react';
import SngpcListingPage, {
    type SngpcColumn,
    type SngpcFilterField,
} from '../Components/sngpc/SngpcListingPage';
import type { SngpcFormField } from '../Components/sngpc/SngpcEntityOffcanvas';
import { StatusBadge } from '../Components/ui/Badge';

type Entity = Record<string, unknown> & { id?: number | string };

function text(value: unknown, fallback = '-') {
    return value === null || value === undefined || value === '' ? fallback : String(value);
}

function nested(row: Entity, relation: string, field = 'nome') {
    const value = row[relation];
    if (!value || typeof value !== 'object') return '-';
    return text((value as Record<string, unknown>)[field]);
}

function date(value: unknown) {
    if (!value) return '-';
    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? text(value) : parsed.toLocaleDateString('pt-BR');
}

function number(value: unknown) {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '-';
}

const yesNoOptions = [
    { value: '1', label: 'Sim' },
    { value: '0', label: 'Não' },
];

const activeOptions = [
    { value: '1', label: 'Ativos' },
    { value: '0', label: 'Inativos' },
];

const stateOptions = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']
    .map((uf) => ({ value: uf, label: uf }));

const controlListOptions = ['A1', 'A2', 'A3', 'B1', 'B2', 'C1', 'C2', 'C3', 'C4', 'C5', 'D1', 'D2', 'ANTIMICROBIANO']
    .map((value) => ({ value, label: value }));

const doctorEditFields: SngpcFormField[] = [
    { name: 'nome', label: 'Nome', required: true },
    { name: 'crm', label: 'CRM', required: true },
    { name: 'uf_crm', label: 'UF do CRM', type: 'select', required: true, options: stateOptions },
    { name: 'cpf', label: 'CPF' },
    { name: 'especialidade', label: 'Especialidade' },
    { name: 'telefone', label: 'Telefone', type: 'tel' },
    { name: 'email', label: 'E-mail', type: 'email' },
    { name: 'endereco', label: 'Endereço', fullWidth: true },
    { name: 'numero', label: 'Número' },
    { name: 'bairro', label: 'Bairro' },
    { name: 'cidade', label: 'Cidade' },
    { name: 'uf', label: 'UF do endereço', type: 'select', options: stateOptions },
    { name: 'cep', label: 'CEP' },
    { name: 'ativo', label: 'Cadastro ativo', type: 'checkbox', defaultValue: true, fullWidth: true },
];

const patientEditFields: SngpcFormField[] = [
    { name: 'nome', label: 'Nome', required: true },
    { name: 'cpf', label: 'CPF' },
    { name: 'data_nascimento', label: 'Data de nascimento', type: 'date' },
    { name: 'sexo', label: 'Sexo', type: 'select', options: [{ value: 'F', label: 'Feminino' }, { value: 'M', label: 'Masculino' }, { value: 'O', label: 'Outro' }] },
    { name: 'telefone', label: 'Telefone', type: 'tel' },
    { name: 'email', label: 'E-mail', type: 'email' },
    { name: 'endereco', label: 'Endereço', fullWidth: true },
    { name: 'numero', label: 'Número' },
    { name: 'complemento', label: 'Complemento' },
    { name: 'bairro', label: 'Bairro' },
    { name: 'cidade', label: 'Cidade' },
    { name: 'uf', label: 'UF', type: 'select', options: stateOptions },
    { name: 'cep', label: 'CEP' },
    { name: 'observacoes', label: 'Observações', type: 'textarea', fullWidth: true },
    { name: 'ativo', label: 'Cadastro ativo', type: 'checkbox', defaultValue: true, fullWidth: true },
];

const inventoryFields: SngpcFormField[] = [
    { name: 'data_inventario', label: 'Data do inventário', type: 'date', required: true, defaultValue: new Date().toISOString().slice(0, 10) },
    { name: 'tipo', label: 'Tipo', type: 'select', required: true, defaultValue: 'ANUAL', options: ['ANUAL', 'TRIMESTRAL', 'EXTRAORDINARIO'].map((value) => ({ value, label: value })) },
    { name: 'observacoes', label: 'Observações', type: 'textarea', fullWidth: true },
];

const productFilters: SngpcFilterField[] = [
    { name: 'lista_controle', label: 'Lista', options: controlListOptions },
    { name: 'ativo', label: 'Situação', options: activeOptions },
];

const doctorFilters: SngpcFilterField[] = [
    { name: 'uf', label: 'UF', options: stateOptions },
    { name: 'especialidade', label: 'Especialidade', options: ['Psiquiatria', 'Neurologia', 'Clínica Médica', 'Geriatria', 'Pediatria', 'Oncologia'].map((value) => ({ value, label: value })) },
    { name: 'ativo', label: 'Situação', options: activeOptions },
];

const patientFilters: SngpcFilterField[] = [
    { name: 'uf', label: 'UF', options: stateOptions },
    { name: 'sexo', label: 'Sexo', options: [{ value: 'F', label: 'Feminino' }, { value: 'M', label: 'Masculino' }, { value: 'O', label: 'Outro' }] },
    { name: 'ativo', label: 'Situação', options: activeOptions },
];

const periodFilters: SngpcFilterField[] = [
    { name: 'data_inicial', label: 'Data inicial', type: 'date' },
    { name: 'data_final', label: 'Data final', type: 'date' },
];

const productColumns: SngpcColumn<Entity>[] = [
    { id: 'codigo', label: 'Código', render: (row) => text(row.codigo) },
    { id: 'descricao', label: 'Descrição', primary: true, render: (row) => text(row.descricao) },
    { id: 'principio', label: 'Princípio ativo', hideOnMobile: true, render: (row) => text(row.principio_ativo) },
    { id: 'registro', label: 'Registro MS', hideOnMobile: true, render: (row) => text(row.registro_ms) },
    { id: 'lista', label: 'Lista', align: 'center', render: (row) => <StatusBadge label={text(row.lista_controle)} tone="info" /> },
    { id: 'situacao', label: 'Situação', align: 'center', render: (row) => <StatusBadge label={row.ativo === false ? 'Inativo' : 'Ativo'} tone={row.ativo === false ? 'neutral' : 'success'} /> },
];

const medicoColumns: SngpcColumn<Entity>[] = [
    { id: 'nome', label: 'Nome', primary: true, render: (row) => text(row.nome) },
    { id: 'crm', label: 'CRM', render: (row) => text(row.crm) },
    { id: 'uf', label: 'UF', align: 'center', render: (row) => text(row.uf_crm) },
    { id: 'especialidade', label: 'Especialidade', hideOnMobile: true, render: (row) => text(row.especialidade) },
    { id: 'telefone', label: 'Telefone', hideOnMobile: true, render: (row) => text(row.telefone) },
    { id: 'situacao', label: 'Situação', align: 'center', render: (row) => <StatusBadge label={row.ativo === false ? 'Inativo' : 'Ativo'} tone={row.ativo === false ? 'neutral' : 'success'} /> },
];

const pacienteColumns: SngpcColumn<Entity>[] = [
    { id: 'nome', label: 'Nome', primary: true, render: (row) => text(row.nome) },
    { id: 'cpf', label: 'CPF', render: (row) => text(row.cpf) },
    { id: 'nascimento', label: 'Nascimento', hideOnMobile: true, render: (row) => date(row.data_nascimento) },
    { id: 'telefone', label: 'Telefone', render: (row) => text(row.telefone) },
    { id: 'cidade', label: 'Cidade / UF', hideOnMobile: true, render: (row) => `${text(row.cidade)} / ${text(row.uf)}` },
    { id: 'situacao', label: 'Situação', align: 'center', render: (row) => <StatusBadge label={row.ativo === false ? 'Inativo' : 'Ativo'} tone={row.ativo === false ? 'neutral' : 'success'} /> },
];

const entradaColumns: SngpcColumn<Entity>[] = [
    { id: 'data', label: 'Data', render: (row) => date(row.data_entrada) },
    { id: 'nota', label: 'Nota fiscal', render: (row) => text(row.numero_nota) },
    { id: 'produto', label: 'Produto', primary: true, render: (row) => nested(row, 'produto', 'descricao') },
    { id: 'fornecedor', label: 'Fornecedor', hideOnMobile: true, render: (row) => nested(row, 'fornecedor') },
    { id: 'lote', label: 'Lote', render: (row) => text(row.lote) },
    { id: 'quantidade', label: 'Quantidade', align: 'right', render: (row) => number(row.quantidade) },
];

const saidaColumns: SngpcColumn<Entity>[] = [
    { id: 'data', label: 'Data', render: (row) => date(row.data_saida) },
    { id: 'tipo', label: 'Tipo', render: (row) => text(row.tipo_saida) },
    { id: 'produto', label: 'Produto', primary: true, render: (row) => nested(row, 'produto', 'descricao') },
    { id: 'paciente', label: 'Paciente', hideOnMobile: true, render: (row) => text(row.nome_paciente, nested(row, 'cliente')) },
    { id: 'lote', label: 'Lote', render: (row) => text(row.lote) },
    { id: 'quantidade', label: 'Quantidade', align: 'right', render: (row) => number(row.quantidade) },
];

const receitaColumns: SngpcColumn<Entity>[] = [
    { id: 'data', label: 'Emissão', render: (row) => date(row.data_emissao) },
    { id: 'numero', label: 'Nº receita', render: (row) => text(row.numero_receita) },
    { id: 'paciente', label: 'Paciente', primary: true, render: (row) => text(row.nome_paciente) },
    { id: 'medico', label: 'Médico', hideOnMobile: true, render: (row) => nested(row, 'medico') },
    { id: 'tipo', label: 'Tipo', render: (row) => text(row.tipo_receita) },
    { id: 'retida', label: 'Retida', align: 'center', render: (row) => <StatusBadge label={row.retida ? 'Sim' : 'Não'} tone={row.retida ? 'success' : 'neutral'} /> },
];

const estoqueColumns: SngpcColumn<Entity>[] = [
    { id: 'produto', label: 'Produto', primary: true, render: (row) => nested(row, 'produto', 'descricao') },
    { id: 'lote', label: 'Lote', render: (row) => text(row.lote) },
    { id: 'validade', label: 'Validade', render: (row) => date(row.data_validade) },
    { id: 'quantidade', label: 'Quantidade', align: 'right', render: (row) => number(row.quantidade) },
    { id: 'valor', label: 'Valor médio', align: 'right', hideOnMobile: true, render: (row) => `R$ ${number(row.valor_medio)}` },
];

const saldoColumns: SngpcColumn<Entity>[] = [
    { id: 'produto', label: 'Produto', primary: true, render: (row) => text(row.descricao, nested(row, 'produto', 'descricao')) },
    { id: 'codigo', label: 'Código', render: (row) => text(row.codigo) },
    { id: 'unidade', label: 'Unidade', align: 'center', render: (row) => text(row.unidade) },
    { id: 'saldo', label: 'Saldo atual', align: 'right', render: (row) => number(row.saldo_total) },
    { id: 'valor', label: 'Valor médio', align: 'right', hideOnMobile: true, render: (row) => `R$ ${number(row.valor_medio)}` },
];

const inventarioColumns: SngpcColumn<Entity>[] = [
    { id: 'data', label: 'Data', render: (row) => date(row.data_inventario) },
    { id: 'tipo', label: 'Tipo', render: (row) => text(row.tipo) },
    { id: 'responsavel', label: 'Responsável', primary: true, hideOnMobile: true, render: (row) => nested(row, 'responsavel', 'name') },
    { id: 'finalizacao', label: 'Finalização', hideOnMobile: true, render: (row) => date(row.data_finalizacao) },
    { id: 'status', label: 'Situação', align: 'center', render: (row) => <StatusBadge label={text(row.status)} tone={row.status === 'FINALIZADO' ? 'success' : 'warning'} /> },
];

const exportacaoColumns: SngpcColumn<Entity>[] = [
    { id: 'geracao', label: 'Geração', render: (row) => date(row.data_geracao ?? row.created_at) },
    { id: 'periodo', label: 'Período', primary: true, render: (row) => `${date(row.data_inicial)} a ${date(row.data_final)}` },
    { id: 'tipo', label: 'Tipo', render: (row) => text(row.tipo, 'Movimentação') },
    { id: 'registros', label: 'Registros', align: 'center', hideOnMobile: true, render: (row) => number(row.total_registros) },
    { id: 'protocolo', label: 'Protocolo', hideOnMobile: true, render: (row) => text(row.protocolo_anvisa) },
    { id: 'status', label: 'Situação', align: 'center', render: (row) => <StatusBadge label={text(row.status)} tone={row.status === 'CONCLUIDO' ? 'success' : row.status === 'ERRO' ? 'danger' : 'warning'} /> },
];

export function Produtos() {
    return (
        <SngpcListingPage
            title="Produtos controlados"
            description="Gerencie os medicamentos sujeitos a controle especial."
            icon={Pill}
            endpoint="/produtos"
            columns={productColumns}
            emptyMessage="Nenhum produto controlado encontrado."
            searchPlaceholder="Buscar por código, descrição ou registro MS..."
            actionLabel="Novo produto"
            createPath="/produtos/novo"
            editPath={(row) => `/produtos/${row.id}/editar`}
            filterFields={productFilters}
            canEdit
            canDelete
            deleteName={(row) => text(row.descricao)}
        />
    );
}

export function Medicos() {
    return (
        <SngpcListingPage
            title="Médicos"
            description="Mantenha os prescritores vinculados às receitas controladas."
            icon={Stethoscope}
            endpoint="/medicos"
            columns={medicoColumns}
            emptyMessage="Nenhum médico cadastrado."
            searchPlaceholder="Buscar por nome, CRM ou especialidade..."
            actionLabel="Novo médico"
            filterFields={doctorFilters}
            editFields={doctorEditFields}
            editTitle="médico"
            canEdit
            canDelete
            deleteName={(row) => text(row.nome)}
        />
    );
}

export function Pacientes() {
    return (
        <SngpcListingPage
            title="Pacientes"
            description="Organize os dados dos pacientes atendidos pela farmácia."
            icon={UserRound}
            endpoint="/pacientes"
            columns={pacienteColumns}
            emptyMessage="Nenhum paciente cadastrado."
            searchPlaceholder="Buscar por nome ou CPF..."
            actionLabel="Novo paciente"
            filterFields={patientFilters}
            editFields={patientEditFields}
            editTitle="paciente"
            canEdit
            canDelete
            deleteName={(row) => text(row.nome)}
        />
    );
}

export function Entradas() {
    return (
        <SngpcListingPage
            title="Entradas"
            description="Acompanhe as entradas de produtos controlados no estoque."
            icon={FileInput}
            endpoint="/entradas"
            columns={entradaColumns}
            emptyMessage="Nenhuma entrada registrada."
            searchPlaceholder="Buscar por nota, produto, fornecedor ou lote..."
            actionLabel="Nova entrada"
            createPath="/entradas/nova"
            editPath={(row) => `/entradas/${row.id}/editar`}
            filterFields={[
                { name: 'tipo', label: 'Tipo', options: ['COMPRA', 'TRANSFERENCIA', 'DEVOLUCAO'].map((value) => ({ value, label: value })) },
                ...periodFilters,
            ]}
            canEdit
            canDelete
            deleteName={(row) => `a entrada ${text(row.numero_nota)}`}
        />
    );
}

export function Saidas() {
    return (
        <SngpcListingPage
            title="Saídas"
            description="Consulte dispensações, vendas e demais baixas de estoque."
            icon={FileOutput}
            endpoint="/saidas"
            columns={saidaColumns}
            emptyMessage="Nenhuma saída registrada."
            searchPlaceholder="Buscar por paciente, produto ou lote..."
            actionLabel="Nova saída"
            createPath="/saidas/nova"
            editPath={(row) => `/saidas/${row.id}/editar`}
            filterFields={[
                { name: 'tipo', label: 'Tipo', options: ['DISPENSACAO', 'PERDA', 'USO_INTERNO', 'VENDA', 'TRANSFERENCIA'].map((value) => ({ value, label: value })) },
                ...periodFilters,
            ]}
            canEdit
            canDelete
            deleteName={(row) => `a saída de ${text(row.nome_paciente)}`}
        />
    );
}

export function Receitas() {
    return (
        <SngpcListingPage
            title="Receitas"
            description="Controle as prescrições utilizadas nas dispensações."
            icon={FileCheck2}
            endpoint="/receitas"
            columns={receitaColumns}
            emptyMessage="Nenhuma receita registrada."
            searchPlaceholder="Buscar por receita, paciente ou médico..."
            actionLabel="Nova receita"
            createPath="/receitas/nova"
            editPath={(row) => `/receitas/${row.id}/editar`}
            filterFields={[
                { name: 'tipo', label: 'Tipo', options: ['BRANCA', 'AZUL', 'AMARELA', 'ANTIMICROBIANO', 'ESPECIAL'].map((value) => ({ value, label: value })) },
                { name: 'retida', label: 'Retida', options: yesNoOptions },
                ...periodFilters,
            ]}
            canEdit
            canDelete
            deleteName={(row) => `a receita ${text(row.numero_receita)}`}
        />
    );
}

export function Estoque() {
    return (
        <SngpcListingPage
            title="Estoque controlado"
            description="Visualize quantidades, lotes e validades disponíveis."
            icon={Boxes}
            endpoint="/estoque"
            columns={estoqueColumns}
            emptyMessage="Nenhum produto controlado em estoque."
            searchPlaceholder="Buscar por produto ou lote..."
            filterFields={[
                { name: 'validade', label: 'Validade', options: [
                    { value: 'vencidos', label: 'Vencidos' },
                    { value: '30', label: 'Vence em 30 dias' },
                    { value: '60', label: 'Vence em 60 dias' },
                    { value: '90', label: 'Vence em 90 dias' },
                ] },
            ]}
        />
    );
}

export function Saldo() {
    return (
        <SngpcListingPage
            title="Saldo por produto"
            description="Consulte a posição consolidada do estoque controlado."
            icon={PackageSearch}
            endpoint="/estoque/saldo"
            columns={saldoColumns}
            emptyMessage="Nenhum produto com movimentação."
            searchPlaceholder="Buscar por produto ou código..."
            filterFields={[{ name: 'unidade', label: 'Unidade', options: [{ value: 'UN', label: 'UN' }, { value: 'CX', label: 'CX' }, { value: 'FR', label: 'FR' }] }]}
        />
    );
}

export function Inventario() {
    return (
        <SngpcListingPage
            title="Inventários"
            description="Acompanhe contagens físicas e ajustes do estoque."
            icon={ClipboardCheck}
            endpoint="/inventarios"
            columns={inventarioColumns}
            emptyMessage="Nenhum inventário realizado."
            searchPlaceholder="Buscar por responsável ou período..."
            actionLabel="Novo inventário"
            filterFields={[
                { name: 'tipo', label: 'Tipo', options: ['ANUAL', 'TRIMESTRAL', 'EXTRAORDINARIO'].map((value) => ({ value, label: value })) },
                { name: 'status', label: 'Situação', options: ['EM_ANDAMENTO', 'FINALIZADO', 'CANCELADO'].map((value) => ({ value, label: value })) },
                ...periodFilters,
            ]}
            editFields={inventoryFields}
            editTitle="inventário"
            canEdit={(row) => row.status === 'EM_ANDAMENTO'}
            canDelete={(row) => row.status === 'EM_ANDAMENTO'}
            deleteName={() => 'este inventário em andamento'}
        />
    );
}

export function Relatorios() {
    return (
        <SngpcListingPage
            title="Relatórios enviados"
            pageTitle="Relatórios Enviados"
            description="Consulte os arquivos gerados e os envios realizados à ANVISA."
            icon={FileText}
            endpoint="/exportacoes-xml"
            columns={exportacaoColumns}
            emptyMessage="Nenhum relatório enviado."
            searchPlaceholder="Buscar por período, protocolo ou situação..."
            filterFields={[
                { name: 'status', label: 'Situação', options: ['PENDENTE', 'PROCESSANDO', 'CONCLUIDO', 'ERRO'].map((value) => ({ value, label: value })) },
                { name: 'tipo', label: 'Tipo', options: [{ value: 'MOVIMENTACAO', label: 'Movimentação' }, { value: 'INVENTARIO', label: 'Inventário' }] },
                ...periodFilters,
            ]}
            canDelete
            deleteName={(row) => `o relatório ${text(row.nome_arquivo, text(row.protocolo_anvisa))}`}
        />
    );
}
